import React from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper/Paper';
import {Image, ImageProps, ImageRatioOptions} from '../images';
import {Eyebrow, EyebrowProps} from '../eyebrow';
import {Typography} from '../typography';
import {VerticalSpacer} from '../spacer';
import {CmsButtonPropsWithHoverType, UeLink, WithCaption, WithCaptionProps} from '../../wraps';
import {Button, ClickEventHandler, CmsButtonProps} from '../buttons';
import {MouseHijack} from '../../wraps';
import {AccentColorOptions} from 'epic-ue-theme';
import {Hyperlink, TextLink} from '../links';

const getRadialGradient = (from, to) =>
    `radial-gradient(100% 185.14% at 100% 100%, ${from} 0%, ${to} 100%)`;

const getHoverEffect = (p) => {
    if (p.imageHoverEffect === 'scaleIn') {
        return 'transition: transform 0.5s;transform: scale(1.1);';
    }
};

const ImageCardWrapper = styled.div<{
    imageHoverEffect: ImageHoverEffect;
    showTopAccentColorLine: boolean;
    accentColor: AccentColorOptions;
}>`
    .mui-paper {
        background: none;
        position: relative;
    }
    ${(p) =>
        p.showTopAccentColorLine &&
        `.mui-paper::before {
            position: absolute;
            left: 0;
            top: 0;
            content: '';
            width: 100%;
            height: 2px;
            background: ${p.theme.palette.accent[p.accentColor]};
            background: ${getRadialGradient(
                p.theme.palette.gradient[p.accentColor].from,
                p.theme.palette.gradient[p.accentColor].to
            )};
        }`}
    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }
    & {
        .ue-link-wrap {
            img:hover {
                ${(p) => getHoverEffect(p)}
            }
        }
    }

    & img {
        vertical-align: bottom;
    }
`;

export type ImageHoverEffect = 'mouseHijack' | 'scaleIn';

export interface ImageCardProps {
    accentColor?: AccentColorOptions;
    caption?: WithCaptionProps;
    children?: React.ReactNode;
    bottomEyebrow?: EyebrowProps;
    description?: string;
    eyebrow?: EyebrowProps;
    image?: ImageProps;
    imageHoverEffect?: ImageHoverEffect;
    imageRatioOverride?: ImageRatioOptions;
    link?: CmsButtonPropsWithHoverType;
    links?: CmsButtonProps[];
    onClick?: ClickEventHandler;
    paragraphCaption?: boolean;
    showButton?: boolean;
    showTopAccentColorLine?: boolean;
    title?: string;
    optMaxWidth?: number;
    _type?: string;
    hideBottomPadding?: boolean;
}

export const ImageCard = ({
    accentColor = 'sky',
    caption,
    children,
    bottomEyebrow = {},
    description,
    eyebrow = {},
    image,
    imageHoverEffect = 'mouseHijack',
    imageRatioOverride,
    link = {},
    links,
    onClick,
    paragraphCaption = true,
    showButton,
    title,
    showTopAccentColorLine = false,
    optMaxWidth,
    hideBottomPadding = false
}: ImageCardProps): JSX.Element | null => {
    const validEyebrow = eyebrow && (eyebrow.text || eyebrow.children);
    const validBottomEyebrow = bottomEyebrow && (bottomEyebrow.text || bottomEyebrow.children);
    const validLink = link && link.href;
    const validImage = image && image.src;

    if (
        !validEyebrow &&
        !validBottomEyebrow &&
        !validLink &&
        !validImage &&
        !title &&
        !description &&
        !children &&
        !links?.length
    ) {
        return null;
    }

    const imageEl = validImage ? (
        <WithCaption {...caption}>
            <Image
                {...image}
                fit="cover"
                ratio={imageRatioOverride || image?.ratio}
                optMaxWidth={optMaxWidth}
            />
        </WithCaption>
    ) : null;

    let imageLinkEl: JSX.Element | null = null;
    switch (imageHoverEffect) {
        case 'mouseHijack':
            imageLinkEl = (
                <MouseHijack
                    href={link.href}
                    hoverCursorType={link.hoverCursorType}
                    accentColor={accentColor}>
                    {imageEl}
                </MouseHijack>
            );
            break;
        case 'scaleIn':
            imageLinkEl = <UeLink href={link.href}>{imageEl}</UeLink>;
            break;
        default:
            break;
    }

    return (
        <ImageCardWrapper
            imageHoverEffect={imageHoverEffect}
            showTopAccentColorLine={showTopAccentColorLine}
            accentColor={accentColor}
            className="image-card">
            <Paper elevation={0} square={true} className="mui-paper">
                {validLink && imageEl ? imageLinkEl : null}
                {!validLink && imageEl ? imageEl : null}

                <div className="image-card-content">
                    {validEyebrow || title || description ? (
                        <VerticalSpacer variant="heading" />
                    ) : (
                        ''
                    )}

                    {validEyebrow ? (
                        <>
                            <Eyebrow {...eyebrow} />
                            <VerticalSpacer variant="eyebrow" />
                        </>
                    ) : (
                        ''
                    )}

                    {title ? (
                        <TextLink href={link.href} accentColor={accentColor}>
                            <Typography variant="h3" big={true}>
                                {title}
                            </Typography>
                        </TextLink>
                    ) : (
                        ''
                    )}
                    {description && (
                        <>
                            <VerticalSpacer variant="paragraph" />
                            <Typography variant="p" caption={paragraphCaption}>
                                {description}
                            </Typography>
                        </>
                    )}

                    {validBottomEyebrow ? (
                        <>
                            <VerticalSpacer variant="custom" spacing={3} />
                            <Eyebrow {...bottomEyebrow} />
                        </>
                    ) : (
                        ''
                    )}

                    {showButton && validLink && link.label ? (
                        <>
                            <VerticalSpacer variant="heading" />
                            <Button
                                type={link.type}
                                href={link.href}
                                color={accentColor}
                                onClick={onClick}>
                                {link.label}
                            </Button>
                        </>
                    ) : (
                        ''
                    )}

                    {links && links.length ? (
                        <>
                            <VerticalSpacer variant="custom" spacing={4} />
                            {links.map((link, i) => (
                                <React.Fragment key={link.label}>
                                    <Hyperlink href={link.href} accentColor={accentColor}>
                                        <Typography variant="button">{link.label}</Typography>
                                    </Hyperlink>
                                    {i < links.length - 1 ? (
                                        <VerticalSpacer variant="paragraph" />
                                    ) : (
                                        ''
                                    )}
                                </React.Fragment>
                            ))}
                        </>
                    ) : (
                        ''
                    )}

                    {children ? (
                        <>
                            <VerticalSpacer variant="heading" />
                            {children}
                        </>
                    ) : (
                        ''
                    )}

                    {!hideBottomPadding && (
                        <VerticalSpacer variant="custom" spacing={0} downSmSpacing={2} />
                    )}
                </div>
            </Paper>
        </ImageCardWrapper>
    );
};

ImageCard.displayName = 'ImageCard';
